import { Search } from "lucide-react";
import { useId, useState } from "react";

import {
  DIRECTORY_MIN_QUERY_LENGTH,
  isDirectoryUnavailable,
  useDirectorySearchQuery,
  type DirectorySource,
  type DirectoryUser,
  type Role,
} from "@/api/access";
import { ApiError } from "@/api/client";
import { ROLE_LABEL } from "@/lib/editorAccess";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Field, Input } from "@/ui/Input";
import { joinDescriptionIds } from "@/ui/FieldError";
import { Notice } from "@/ui/Notice";

import { useDebouncedValue } from "./useDebouncedValue";

/** Where the people on screen came from, said plainly beside them. */
export const DIRECTORY_SOURCE_LABEL = {
  "entra-graph": "your organization's Microsoft Entra directory",
  "local-fixture":
    "the local Entra directory test double — synthetic people, not your organization's directory",
} satisfies Record<DirectorySource, string>;

const DEBOUNCE_MS = 300;

/**
 * Search the Entra directory and pick ONE existing person.
 *
 * Server-side, bounded, paged, and started only after the minimum length: the
 * browser never receives the directory, only the people who match what was
 * typed. The results are a radio group rather than a listbox so keyboard
 * operation, selection announcement and focus behaviour come from the
 * platform; the live status line says how many people matched, or why nothing
 * did.
 *
 * `assignedRoles` lets the picker say when a match already holds Editor
 * access, so choosing them becomes an edit of their existing access instead of
 * a grant that the service would answer as a no-op.
 */
export function DirectoryPicker({
  selected,
  onSelect,
  assignedRoles,
  disabled = false,
}: {
  selected: DirectoryUser | null;
  onSelect: (user: DirectoryUser) => void;
  assignedRoles: (subject: string) => readonly Role[] | null;
  disabled?: boolean;
}) {
  const [query, setQuery] = useState("");
  const debounced = useDebouncedValue(query, DEBOUNCE_MS);
  const search = useDirectorySearchQuery(debounced);
  const statusId = useId();
  const groupName = useId();

  const trimmed = query.trim();
  const tooShort = trimmed.length < DIRECTORY_MIN_QUERY_LENGTH;
  const settled = debounced.trim() === trimmed;
  const people = search.data?.pages.flatMap((page) => page.items) ?? [];
  const source = search.data?.pages[0]?.source;
  const unavailable = search.isError && isDirectoryUnavailable(search.error);

  let status: string;
  if (tooShort) {
    status = `Type at least ${DIRECTORY_MIN_QUERY_LENGTH} characters to search.`;
  } else if (
    !settled || search.isPending || search.isPlaceholderData ||
    (search.isFetching && !search.data)
  ) {
    status = "Searching…";
  } else if (search.isError) {
    status = unavailable
      ? ""
      : `The directory search failed${
          search.error instanceof ApiError ? ` (${search.error.code})` : ""
        }.`;
  } else {
    const count = people.length;
    status =
      count === 0
        ? `No people match “${trimmed}”.`
        : `${count} ${count === 1 ? "person" : "people"} found for “${trimmed}”${
            search.hasNextPage ? " — more are available." : "."
          }`;
  }

  return (
    <div className="space-y-3">
      <Field
        label="Find a person in the Entra directory"
        description="Search by display name or organizational email. Only existing accounts can be granted access; nothing here creates one."
      >
        {({ inputId, describedBy }) => (
          <Input
            id={inputId}
            type="search"
            autoComplete="off"
            maxLength={100}
            value={query}
            disabled={disabled}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Name or email"
            leadingIcon={<Search className="h-4 w-4" />}
            aria-describedby={joinDescriptionIds(describedBy, statusId)}
          />
        )}
      </Field>

      <p
        id={statusId}
        role="status"
        aria-live="polite"
        className="text-xs text-muted-foreground"
      >
        {status}
      </p>

      {unavailable ? (
        <Notice role="alert" tone="warning">
          Directory lookup is unavailable. Try again, or ask your platform team
          to check the directory connection if the problem continues.
        </Notice>
      ) : null}

      {!tooShort && settled && search.isError ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || search.isFetching}
          onClick={() => void search.refetch()}
        >
          Try directory search again
        </Button>
      ) : null}

      {!tooShort && settled && !search.isError && !search.isPlaceholderData && people.length > 0 ? (
        <fieldset disabled={disabled}>
          <legend className="sr-only">Search results</legend>
          <ul className="divide-y divide-border rounded-md border border-border bg-card">
            {people.map((user) => {
              const held = assignedRoles(user.subject);
              return (
                <li key={user.subject}>
                  <label className="flex cursor-pointer items-start gap-3 px-3 py-2.5 hover:bg-secondary/60">
                    <input
                      type="radio"
                      name={groupName}
                      value={user.subject}
                      className="mt-1 h-4 w-4 shrink-0 accent-primary"
                      checked={selected?.subject === user.subject}
                      onChange={() => onSelect(user)}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-foreground">
                          {user.displayName}
                        </span>
                        {held && held.length > 0 ? (
                          <Badge tone="accent">
                            Has access:{" "}
                            {held.map((role) => ROLE_LABEL[role]).join(", ")}
                          </Badge>
                        ) : null}
                      </span>
                      {user.email ? (
                        <span className="block text-xs text-muted-foreground">
                          {user.email}
                        </span>
                      ) : null}
                      <span className="block select-all font-mono text-xs text-muted-foreground">
                        {user.subject}
                      </span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
          {search.hasNextPage ? (
            <div className="mt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={search.isFetchingNextPage}
                onClick={() => void search.fetchNextPage()}
              >
                Show more people
              </Button>
            </div>
          ) : null}
        </fieldset>
      ) : null}

      {source ? (
        <p className="text-xs text-muted-foreground">
          Results come from {DIRECTORY_SOURCE_LABEL[source]}.
        </p>
      ) : null}
    </div>
  );
}
